import { ModalLayout } from 'moh-ui';
import useAuth from '@/hooks/useAuth';
import { useUserCouncil } from '@/hooks/useUserCouncil';
import { useRecoilState } from 'recoil';
import { useState, useEffect } from 'react';
import { showEditQuestionState } from '@/atoms';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useGetEnabledLanguages } from '@/services/languages';
import { Select, MultiSelect, Modal, Text } from '@mantine/core';
import { useUpdateQuestion, useDeleteQuestionMedia } from '@/services/questions';
import QuestionMediaUpload from '@/components/common/inputs/QuestionMediaUpload';
import { Button, Group, TextInput, Box, Title, Stack, Tabs, Switch } from '@mantine/core';
import { useGetEducationProgramsByCouncil, useGetQuestionModules, useGetQuestionUnits, useGetBloomsTaxonomyLevels } from '@/services/education-programs';

export default function EditQuestionModal() {
    const [show, setShow] = useRecoilState(showEditQuestionState);
    const { user } = useAuth();
  const council = useUserCouncil();
    const queryClient = useQueryClient();
    const { mutate: updateQuestion, isPending } = useUpdateQuestion();
    const { mutate: deleteMedia, isPending: deletingMedia } = useDeleteQuestionMedia();
    const [translations, setTranslations] = useState<Record<string, string>>({ EN: '' });
    const [options, setOptions] = useState<{ id?: string; translations: Record<string, string>; correct: boolean }[]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['EN']);
    const [showLanguageRemovalModal, setShowLanguageRemovalModal] = useState(false);
    const [languageToRemove, setLanguageToRemove] = useState<string>('');
    const councilId = council?.id;

    // Cascading selection state
    const [educationProgramId, setEducationProgramId] = useState<string | undefined>(undefined);
    const [moduleId, setModuleId] = useState<string | undefined>(undefined);
    const [unitId, setUnitId] = useState<string | undefined>(undefined);
    const [bloomsLevelIds, setBloomsLevelIds] = useState<string[]>([]);
    const [mediaFile, setMediaFile] = useState<File | null>(null);

    // Fetch categories, modules, units, and blooms levels
    const { data: catData } = useGetEducationProgramsByCouncil(councilId);
    const categories = catData?.data || [];
    const { data: modData } = useGetQuestionModules(councilId);
    const allModules = modData?.data || [];
    const modules = educationProgramId ? allModules.filter(mod => mod.educationProgramId === educationProgramId) : allModules;
    const { data: unitData } = useGetQuestionUnits(councilId);
    const allUnits = unitData?.data || [];
    const units = moduleId ? allUnits.filter(unit => unit.moduleId === moduleId) : allUnits;
    const { data: bloomsData } = useGetBloomsTaxonomyLevels(councilId);
    const bloomsLevels = bloomsData?.data || [];

    // Fetch enabled languages
    const { data: languagesData } = useGetEnabledLanguages();
    const enabledLanguages = languagesData?.data || [{ code: 'EN', name: 'English' }];

    // Helper function to get available blooms levels for a unit
    const getAvailableBloomsLevels = (unitId: string) => {
        if (!unitId) return bloomsLevels;
        
        const selectedUnit = allUnits.find(unit => unit.id === unitId);
        if (!selectedUnit) return bloomsLevels;
        
        // Handle new API response schema with bloomsLevels array
        if (selectedUnit.bloomsLevels && selectedUnit.bloomsLevels.length > 0) {
            return bloomsLevels.filter(level => 
                selectedUnit.bloomsLevels!.some(unitLevel => unitLevel.id === level.id)
            );
        }
        
        // Handle old schema with bloomsTaxonomyLevelIds array
        if (selectedUnit.bloomsTaxonomyLevelIds && selectedUnit.bloomsTaxonomyLevelIds.length > 0) {
            return bloomsLevels.filter(level => 
                selectedUnit.bloomsTaxonomyLevelIds!.includes(level.id)
            );
        }
        
        return bloomsLevels;
    };

    // Helper function to extract languages from existing translations
    const extractLanguagesFromTranslations = (translations: Record<string, string>, options: any[]) => {
        const languages = new Set<string>();
        
        // Add languages from question translations
        Object.keys(translations).forEach(lang => {
            if (translations[lang] && translations[lang].trim() !== '') {
                languages.add(lang);
            }
        });
        
        // Add languages from option translations
        options.forEach(option => {
            Object.keys(option.translations || {}).forEach(lang => {
                if (option.translations[lang] && option.translations[lang].trim() !== '') {
                    languages.add(lang);
                }
            });
        });
        
        // Ensure EN is always present
        languages.add('EN');
        
        return Array.from(languages);
    };

    // Helper function to create empty translations for new languages
    const createEmptyTranslations = (languages: string[]) => {
        const emptyTranslations: Record<string, string> = {};
        languages.forEach(lang => {
            emptyTranslations[lang] = '';
        });
        return emptyTranslations;
    };

    // Helper function to update translations for new language selection
    const updateTranslationsForLanguages = (currentTranslations: Record<string, string>, newLanguages: string[]) => {
        const updatedTranslations: Record<string, string> = {};
        
        newLanguages.forEach(lang => {
            updatedTranslations[lang] = currentTranslations[lang] || '';
        });
        
        return updatedTranslations;
    };

    // Handle language selection change
    const handleLanguageSelectionChange = (newLanguages: string[]) => {
        // Ensure EN is always present
        if (!newLanguages.includes('EN')) {
            newLanguages = ['EN', ...newLanguages];
        }
        
        // Check if any language is being removed
        const removedLanguages = selectedLanguages.filter(lang => !newLanguages.includes(lang));
        
        if (removedLanguages.length > 0) {
            // Show confirmation modal for language removal
            setLanguageToRemove(removedLanguages[0]);
            setShowLanguageRemovalModal(true);
            return;
        }
        
        // Add new languages
        setSelectedLanguages(newLanguages);
        
        // Update question translations
        setTranslations(prev => updateTranslationsForLanguages(prev, newLanguages));
        
        // Update option translations
        setOptions(prev => prev.map(option => ({
            ...option,
            translations: updateTranslationsForLanguages(option.translations, newLanguages)
        })));
    };

    // Confirm language removal
    const confirmLanguageRemoval = () => {
        const newLanguages = selectedLanguages.filter(lang => lang !== languageToRemove);
        setSelectedLanguages(newLanguages);
        
        // Remove language from question translations
        setTranslations(prev => {
            const updated = { ...prev };
            delete updated[languageToRemove];
            return updated;
        });
        
        // Remove language from option translations
        setOptions(prev => prev.map(option => {
            const updated = { ...option.translations };
            delete updated[languageToRemove];
            return { ...option, translations: updated };
        }));
        
        setShowLanguageRemovalModal(false);
        setLanguageToRemove('');
    };

    useEffect(() => {
        if (show?.question) {
            // Clear any previously selected media when a question is loaded
            setMediaFile(null);

            // Initialize translations with existing data or default to EN
            const existingTranslations = show.question.translations || { EN: '' };
            setTranslations(existingTranslations);
            
            // Initialize options with translations
            const existingOptions = show.question.options.map(opt => ({
                id: opt.id,
                translations: opt.translations || { EN: '' },
                correct: opt.correct || false,
            }));
            setOptions(existingOptions);
            
            // Extract languages from existing translations
            const existingLanguages = extractLanguagesFromTranslations(existingTranslations, existingOptions);
            setSelectedLanguages(existingLanguages);
            
            setEducationProgramId(show.question.educationProgramId);
            setModuleId(show.question.moduleId);
            setUnitId(show.question.unitId);
            
            // Handle both bloomsLevelIds (array of strings) and bloomsLevels (array of objects)
            if (show.question.bloomsLevelIds && show.question.bloomsLevelIds.length > 0) {
                setBloomsLevelIds(show.question.bloomsLevelIds);
            } else if (show.question.bloomsLevels && show.question.bloomsLevels.length > 0) {
                setBloomsLevelIds(show.question.bloomsLevels.map(level => level.id));
            } else {
                setBloomsLevelIds([]);
            }
        }
    }, [show]);

    function handleTranslationChange(language: string, value: string) {
        setTranslations(prev => ({
            ...prev,
            [language]: value
        }));
    }

    function handleOptionChange(index: number, field: 'correct', value: boolean) {
        if (field === 'correct' && value === true) {
            // If marking an option as correct, set all other options to false
            setOptions(prev => prev.map((option, i) => ({
                ...option,
                correct: i === index ? true : false
            })));
        } else {
            // For other fields or when setting to false, just update the specific option
            setOptions(prev => prev.map((option, i) => 
                i === index ? { ...option, [field]: value } : option
            ));
        }
    }

    function handleOptionTranslationChange(optionIndex: number, language: string, value: string) {
        setOptions(prev => prev.map((option, i) => 
            i === optionIndex ? {
                ...option,
                translations: {
                    ...option.translations,
                    [language]: value
                }
            } : option
        ));
    }

    function addOption() {
        const emptyTranslations = createEmptyTranslations(selectedLanguages);
        setOptions(prev => [...prev, { translations: emptyTranslations, correct: false }]);
    }

    function removeOption(index: number) {
        setOptions(prev => prev.filter((_, i) => i !== index));
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!show?.question) return;
        if (!educationProgramId || !moduleId || !unitId) {
            notifications.show({ title: 'Validation Error', message: 'Please select category, module, and unit', color: 'red' });
            return;
        }

        // Validate that EN translation exists
        if (!translations.EN || translations.EN.trim() === '') {
            notifications.show({ title: 'Validation Error', message: 'English translation is required', color: 'red' });
            return;
        }

        // Validate that all options have EN translations
        const invalidOptions = options.filter(opt => !opt.translations.EN || opt.translations.EN.trim() === '');
        if (invalidOptions.length > 0) {
            notifications.show({ title: 'Validation Error', message: 'All options must have English translations', color: 'red' });
            return;
        }

        // Prepare options: include id only for existing options; omit id for new ones
        const cleanedOptions = options.map((opt) => {
            const base = {
                translations: opt.translations,
                correct: opt.correct,
            };
            return opt.id ? { id: opt.id, ...base } : base;
        });

        const questionData = {
            ...show.question,
            translations,
            options: cleanedOptions,
            educationProgramId,
            moduleId,
            unitId,
            bloomsLevelIds,
            media: mediaFile || undefined,
        };
        updateQuestion(questionData, {
            onSuccess: () => {
                notifications.show({
                    title: 'Success',
                    message: 'Question updated successfully',
                    color: 'green',
                });
                queryClient.invalidateQueries({ queryKey: ['questions', council?.id] });
                // Clear media state after successful update and close
                setMediaFile(null);
                setShow(null);
            },
            onError: (error) => {
                notifications.show({
                    title: 'Error',
                    message: error.response?.data?.message || 'Failed to update question',
                    color: 'red',
                });
            },
        });
    }

    // Get available languages for selection (excluding already selected ones)
    const getAvailableLanguagesForSelection = () => {
        return enabledLanguages.filter(lang => !selectedLanguages.includes(lang.code));
    };

    return (
        <>
            <ModalLayout
                open={Boolean(show?.show)}
                onClose={() => { setMediaFile(null); setShow(null); }}
                title="Edit Question"
                size="xl"
            >
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <Group grow>
                        <Select
                            label="Category"
                            value={educationProgramId || ''}
                            onChange={val => { setEducationProgramId(val || undefined); setModuleId(undefined); setUnitId(undefined); }}
                            data={categories.map(cat => ({ value: cat.id, label: cat.name }))}
                            required
                        />
                        <Select
                            label="Module"
                            value={moduleId || ''}
                            onChange={val => { setModuleId(val || undefined); setUnitId(undefined); }}
                            data={modules.map(mod => ({ value: mod.id, label: mod.name }))}
                            disabled={!educationProgramId}
                            required
                        />
                        <Select
                            label="Unit"
                            value={unitId || ''}
                            onChange={val => setUnitId(val || undefined)}
                            data={units.map(unit => ({ value: unit.id, label: unit.name }))}
                            disabled={!moduleId}
                            required
                        />
                    </Group>

                    <MultiSelect
                        label="Bloom's Taxonomy Levels"
                        value={bloomsLevelIds}
                        onChange={setBloomsLevelIds}
                        data={getAvailableBloomsLevels(unitId || '').map(level => ({ value: level.id, label: `${level.level}. ${level.name}` }))}
                        disabled={!unitId}
                        placeholder="Select Bloom's taxonomy levels"
                    />

                    {/* Language Selection */}
                    <MultiSelect
                        label="Languages"
                        value={selectedLanguages}
                        onChange={handleLanguageSelectionChange}
                        data={enabledLanguages.map(lang => ({ value: lang.code, label: lang.name }))}
                        placeholder="Select languages for translations"
                        description="English is always required and cannot be removed"
                    />

                    {/* Question Translations */}
                    <Box>
                        <Title order={6} mb="sm">Question Text</Title>
                        <Tabs defaultValue="EN">
                            <Tabs.List>
                                {selectedLanguages.map(langCode => {
                                    const lang = enabledLanguages.find(l => l.code === langCode);
                                    return (
                                        <Tabs.Tab key={langCode} value={langCode}>
                                            {lang?.name || langCode}
                                        </Tabs.Tab>
                                    );
                                })}
                            </Tabs.List>
                            {selectedLanguages.map(langCode => {
                                const lang = enabledLanguages.find(l => l.code === langCode);
                                return (
                                    <Tabs.Panel key={langCode} value={langCode} pt="xs">
                                        <TextInput
                                            label={`Question in ${lang?.name || langCode}`}
                                            value={translations[langCode] || ''}
                                            onChange={(e) => handleTranslationChange(langCode, e.target.value)}
                                            required={langCode === 'EN'}
                                            placeholder={`Enter question in ${lang?.name || langCode}...`}
                                        />
                                    </Tabs.Panel>
                                );
                            })}
                        </Tabs>
                    </Box>

                    {/* Question Media */}
                    <QuestionMediaUpload
                        value={mediaFile}
                        existingMedia={show?.question?.questionMediaPath || show?.question?.mediaPath ? {
                            mediaPath: (show.question.questionMediaPath || show.question.mediaPath)!,
                            originalFilename: show.question.mediaInfo?.originalFilename || 'question-media',
                            contentType: show.question.mediaInfo?.contentType || 'image/jpeg',
                            size: show.question.mediaInfo?.size || 0,
                        } : undefined}
                        onChange={setMediaFile}
                        onRemove={() => {
                            if (show?.question?.id) {
                                deleteMedia({
                                    questionId: show.question.id,
                                    councilId: council?.id
                                }, {
                                    onSuccess: () => {
                                        notifications.show({
                                            title: 'Success',
                                            message: 'Media removed successfully',
                                            color: 'green',
                                        });
                                    },
                                });
                            }
                        }}
                        questionId={show?.question?.id}
                        disabled={isPending || deletingMedia}
                    />

                    {/* Options */}
                    <Box>
                        <Title order={6} mb="sm">Options</Title>
                        <Stack gap="md">
                            {options.map((option, idx) => (
                                <Box key={idx} p="md" style={{ border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    <Group justify="space-between" mb="sm">
                                        <Title order={6}>Option {idx + 1}</Title>
                                        <Group gap="xs">
                                            <Switch
                                                label="Correct"
                                                checked={option.correct}
                                                onChange={(e) => handleOptionChange(idx, 'correct', e.currentTarget.checked)}
                                            />
                                            {options.length > 2 && (
                                                <Button
                                                    size="xs"
                                                    color="red"
                                                    variant="outline"
                                                    onClick={() => removeOption(idx)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Group>
                                    </Group>

                                    <Tabs defaultValue="EN">
                                        <Tabs.List>
                                            {selectedLanguages.map(langCode => {
                                                const lang = enabledLanguages.find(l => l.code === langCode);
                                                return (
                                                    <Tabs.Tab key={langCode} value={langCode}>
                                                        {lang?.name || langCode}
                                                    </Tabs.Tab>
                                                );
                                            })}
                                        </Tabs.List>
                                        {selectedLanguages.map(langCode => {
                                            const lang = enabledLanguages.find(l => l.code === langCode);
                                            return (
                                                <Tabs.Panel key={langCode} value={langCode} pt="xs">
                                                    <TextInput
                                                        label={`Option ${idx + 1} in ${lang?.name || langCode}`}
                                                        value={option.translations[langCode] || ''}
                                                        onChange={(e) => handleOptionTranslationChange(idx, langCode, e.target.value)}
                                                        required={langCode === 'EN'}
                                                        placeholder={`Enter option ${idx + 1} in ${lang?.name || langCode}...`}
                                                    />
                                                </Tabs.Panel>
                                            );
                                        })}
                                    </Tabs>
                                </Box>
                            ))}
                            <Button onClick={addOption} variant="outline">
                                Add Option
                            </Button>
                        </Stack>
                    </Box>

                    <Group justify="flex-end">
                        <Button variant="outline" onClick={() => { setMediaFile(null); setShow(null); }}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isPending}>
                            Update Question
                        </Button>
                    </Group>
                </form>
            </ModalLayout>

            {/* Language Removal Confirmation Modal */}
            <Modal
                opened={showLanguageRemovalModal}
                onClose={() => setShowLanguageRemovalModal(false)}
                title="Remove Language"
                size="sm"
            >
                <Stack gap="md">
                    <Text>
                        Are you sure you want to remove the language "{languageToRemove}"? 
                        This will delete all translations for this language from both the question and all options.
                    </Text>
                    <Group justify="flex-end">
                        <Button variant="outline" onClick={() => setShowLanguageRemovalModal(false)}>
                            Cancel
                        </Button>
                        <Button color="red" onClick={confirmLanguageRemoval}>
                            Remove Language
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
